import { memo, useCallback, useState } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Modal from '@amzn/awsui-components-core/modal';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Textarea from '@amzn/awsui-components-core/textarea';

import styles from './FreeTextFeedbackModal.module.scss';

const MAX_COMMENT_LENGTH = 500;

export interface FreeTextFeedbackModalProps {
  readonly visible: boolean;
  readonly onSubmit: (comments: string) => void;
  readonly onDismiss: () => void;
}

const FreeTextFeedbackModal = memo(function FreeTextFeedbackModal({
  visible,
  onSubmit,
  onDismiss,
}: FreeTextFeedbackModalProps) {
  const [comments, setComments] = useState('');

  const canSubmit = comments.trim().length > 0;

  const handleChange = useCallback(({ detail }: { detail: { value: string } }) => {
    if (detail.value.length <= MAX_COMMENT_LENGTH) {
      setComments(detail.value);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit(comments.trim());
    setComments('');
  }, [canSubmit, comments, onSubmit]);

  const handleDismiss = useCallback(() => {
    setComments('');
    onDismiss();
  }, [onDismiss]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeAriaLabel="Close feedback modal"
      header="How can we improve?"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label="Tell us more"
        constraintText={
          <div className={styles.constraintText}>
            <span>Maximum {MAX_COMMENT_LENGTH} characters</span>
            <span>
              {comments.length}/{MAX_COMMENT_LENGTH}
            </span>
          </div>
        }
      >
        <Textarea
          value={comments}
          onChange={handleChange}
          placeholder="Please share any details on how we can improve."
          rows={4}
        />
      </FormField>
    </Modal>
  );
});

export default FreeTextFeedbackModal;
